import React from 'react';
import PropTypes from 'prop-types';
import { DateInput as BPDateInput } from '@blueprintjs/datetime';
import classnames from 'classnames';
import { addYears } from 'date-fns';
import { parse, format, parseLocal } from '@hobs/utils/formatWithTimeZone';
import { WithErrorLabel } from '@hobs/components/FormError';
import '@blueprintjs/datetime/lib/css/blueprint-datetime.css';
import objectPath from 'object-path';
import Icon from '@hobs/components/Icon';
import { DATE_FORMAT } from '@hobs/utils/constants';
import InputLabel from '../InputLabel';
import Styles from './DateInput.module.scss';

const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const DateInput = ({
  field,
  setFieldValue = () => {},
  children,
  disabled,
  noMargin,
  indicationMessage,
  minDate = new Date(2015, 1, 1),
  maxDate = addYears(new Date(), 10),
  wrapperClass,
  timePrecision = null,
  initialMonth = null,
  onChange = () => {},
  popoverProps
}) => {
  const jsDateFormatter = {
    formatDate: date => {
      return format(date, DATE_FORMAT);
    },
    parseDate: str => {
      return parse(str);
    },
    placeholder: format(new Date(), DATE_FORMAT)
  };

  const handleChange = selectedDate => {
    const formattedDate =
      (selectedDate === '-' &&
        format(parseFn(selectedDate), 'yyyy-MM-dd') === '-') ||
      !selectedDate
        ? null
        : format(parseFn(selectedDate), 'yyyy-MM-dd');

    setFieldValue(name, formattedDate);

    if (onChange) {
      onChange(formattedDate);
    }
  };

  const name = objectPath.get(field, 'name');
  const value = objectPath.get(field, 'value');

  let parseFn = date => date;

  if (value && parse(value) !== 'Invalid Date' && parse(value) !== '-') {
    parseFn = parse;
  } else if (value && parseLocal(value) !== 'Invalid Date') {
    parseFn = parseLocal;
  }

  return (
    <div
      className={classnames(Styles['form-item'], wrapperClass, {
        [Styles['no-margin']]: noMargin
      }, 'stop-drag')}
    >
      <InputLabel indicationMessage={indicationMessage}>{children}</InputLabel>
      <div data-input="date-input" className={Styles.input}>
        <BPDateInput
          timePrecision={timePrecision}
          disabled={disabled}
          {...jsDateFormatter}
          popoverProps={{
            portalClassName: Styles.datepicker,
            position: 'bottom',
            modifiers: {
              arrow: false
            },
            ...popoverProps
          }}
          dayPickerProps={{
            firstDayOfWeek: 1,
            weekdaysShort: WEEKDAYS_SHORT
          }}
          maxDate={maxDate}
          minDate={minDate}
          inputProps={{ name }}
          onChange={handleChange}
          value={value && value !== '' && value !== '-' ? parseFn(value) : null}
          initialMonth={initialMonth}
        />
        <span className={Styles.icon}>
          <Icon icon="calendar" />
        </span>
      </div>
    </div>
  );
};

DateInput.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  noMargin: PropTypes.bool,
  form: PropTypes.object,
  field: PropTypes.object,
  setFieldValue: PropTypes.func,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  type: PropTypes.oneOf(['text', 'password', 'date']),
  timePrecision: PropTypes.oneOf([null, 'millisecond', 'minute', 'second']),
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string
  ]),
  indicationMessage: PropTypes.object,
  popoverProps: PropTypes.object,
  wrapperClass: PropTypes.string,
  onChange: PropTypes.func,
  initialMonth: PropTypes.any
};

export default WithErrorLabel(DateInput);
